import React, { useMemo, useState } from 'react';
import FuseAnimate from '@fuse/core/FuseAnimate';
import FusePageCarded from '@fuse/core/FusePageCarded';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import PeopleIcon from '@mui/icons-material/People';
import moment from 'moment';

import StatsCards from 'allGlobalComponents/StatsCards';
import TableOmniNew from 'allGlobalComponents/TableOmniNew';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import { useDateRange, usePagination } from 'app/hooks';
import { exportToExcel, formatDate } from 'app/utility/ExportToExcelUtility';
import {
	showErrorSwal,
	showSuccessSwal,
	showWarningSwal
} from 'app/utility/Swalutility';

const useStyles = makeStyles(theme => ({
	container: {
		width: '100%'
	}
}));

function LeadsTab({ leads = [] }) {
	const classes = useStyles();
	const { startDate, endDate, handleDateChange } = useDateRange(false);
	const { currentParams, setCurrentParams, resetPage } = usePagination(10);

	const [filterValues, setFilterValues] = useState({ search: '' });
	const [isExporting, setIsExporting] = useState(false);

	const filteredLeads = useMemo(() => {
		const search = (filterValues.search || '').trim().toLowerCase();
		const start = startDate ? moment(startDate) : null;
		const end = endDate ? moment(endDate) : null;

		return leads.filter(lead => {
			const captured = lead.capturedAt ? moment(lead.capturedAt) : null;
			if (start && captured && captured.isBefore(start)) return false;
			if (end && captured && captured.isAfter(end)) return false;

			if (search) {
				const haystack = [lead.name, lead.email, lead.company]
					.filter(Boolean)
					.join(' ')
					.toLowerCase();
				if (!haystack.includes(search)) return false;
			}
			return true;
		});
	}, [leads, filterValues.search, startDate, endDate]);

	const stats = useMemo(() => {
		const today = moment().startOf('day');
		return {
			total: leads.length,
			today: leads.filter(l => l.capturedAt && moment(l.capturedAt).isSameOrAfter(today)).length,
			withEmail: leads.filter(l => l.email && l.email.trim()).length,
			withPhone: leads.filter(l => l.phone && l.phone.trim()).length
		};
	}, [leads]);

	const pagedLeads = useMemo(() => {
		const start = currentParams.page * currentParams.limit;
		return filteredLeads.slice(start, start + currentParams.limit);
	}, [filteredLeads, currentParams.page, currentParams.limit]);

	const onDateChange = dates => {
		handleDateChange(dates);
		resetPage();
	};

	const handleFiltersApply = filters => {
		setFilterValues(filters);
		resetPage();
	};

	const handleFiltersClear = filters => {
		setFilterValues(filters);
		resetPage();
	};

	const exportColumns = [
		{ header: 'S.No', key: '_index' },
		{ header: 'Name', key: 'name' },
		{ header: 'Email', key: 'email' },
		{ header: 'Phone', key: 'phone' },
		{ header: 'Company', key: 'company' },
		{ header: 'Captured At', key: 'capturedAt', transform: val => formatDate(val) }
	];

	const onExport = () => {
		if (!filteredLeads.length) {
			showWarningSwal('No Leads', 'There are no leads matching the current filters to export.');
			return;
		}
		setIsExporting(true);
		try {
			const result = exportToExcel({
				fileNamePrefix: 'voice_leads',
				dateRange: { startDate, endDate },
				sheets: [{ name: 'Leads', data: filteredLeads, columns: exportColumns }]
			});
			if (result.success) {
				showSuccessSwal('Export Ready', `Exported ${filteredLeads.length} leads.`);
			} else {
				showErrorSwal('Export Failed', result.error || 'Could not export leads.');
			}
		} catch (e) {
			showErrorSwal('Export Failed', e.message || 'Could not export leads.');
		} finally {
			setIsExporting(false);
		}
	};

	const filterFields = [
		{
			name: 'search',
			label: 'Search',
			type: 'text',
			placeholder: 'Search name, email, or company...',
			defaultValue: ''
		}
	];

	const headCells = [
		{ id: 'id', numeric: true, disablePadding: false, label: 'ID' },
		{ id: 'name', numeric: false, disablePadding: false, label: 'Name', tooltip: true },
		{ id: 'email', numeric: false, disablePadding: false, label: 'Email', msg: true, truncateLength: 30 },
		{ id: 'phone', numeric: false, disablePadding: false, label: 'Phone' },
		{ id: 'company', numeric: false, disablePadding: false, label: 'Company', tooltip: true },
		{ id: 'capturedAt', numeric: false, disablePadding: false, label: 'Captured At', dt: true }
	];

	const statsItems = [
		{ label: 'Total Leads', value: stats.total },
		{ label: "Today's Leads", value: stats.today },
		{ label: 'Leads with Email', value: stats.withEmail },
		{ label: 'Leads with Phone', value: stats.withPhone }
	];

	return (
		<FusePageCarded
			header={
				<div className="flex flex-1 w-full items-center justify-between flex-wrap">
					<div className="hidden sm:flex items-center">
						<FuseAnimate animation="transition.expandIn" delay={300}>
							<PeopleIcon fontSize="large" />
						</FuseAnimate>
						<FuseAnimate animation="transition.slideLeftIn" delay={300}>
							<Typography className="flex mx-0 sm:mx-12" variant="h6">
								<span style={{ fontSize: '15px' }}>Captured Leads</span>
							</Typography>
						</FuseAnimate>
					</div>
				</div>
			}
			content={
				<div className={`w-full mx-auto ${classes.container}`}>
					<StatsCards items={statsItems} isLoading={false} />

					<DateFilterWithExport
						startDate={startDate}
						endDate={endDate}
						onDateChange={onDateChange}
						showExport={true}
						onExport={onExport}
						isExporting={isExporting}
						filterFields={filterFields}
						filterValues={filterValues}
						onFiltersApply={handleFiltersApply}
						onFiltersClear={handleFiltersClear}
					/>

					<TableOmniNew
						rows={pagedLeads}
						headCells={headCells}
						perPageRows={currentParams.limit}
						setCurrentParams={setCurrentParams}
						totalItems={filteredLeads.length}
						curPage={currentParams.page}
						loading={false}
						emptyMessage="No Leads Yet — they'll appear here as the assistant captures them."
					/>
				</div>
			}
		/>
	);
}

export default React.memo(LeadsTab);
